import { useDraggable } from '@dnd-kit/core';
import React, { forwardRef, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { Icon, Image, Text } from '../../ui';
import { StyledBadge } from '../Card/ImageCard/ImageCard.style';
import { ActionButtons, AssetActionButtons } from './AssetActionButtons';
import {
  StyleContentWrapper,
  StyledImage,
  StyledOverlayWrapper,
  StyledTextActionButtons,
  StyledTextLabelWrapper,
  StyledTextWrapper,
  StyledWrapper,
} from './AssetCard.style';

export const sourceFallback =
  'https://static.desygner.com/assets/img/placeholders/imageplaceholder_new.svg';

export interface AssetAction {
  source: string;
  action: string | undefined;
  details: any;
}

export interface AssetCardProps {
  title?: string;
  source: string | string[];
  draggable?: boolean;
  actionButtons?: ActionButtons[];
  data: AssetAction['details'];
  isQa?: boolean;
  onClick: (action: AssetAction) => void;
  badge?: string;
}

export const AssetCard = memo(
  forwardRef(function AssetCard(
    { title, draggable, actionButtons, source, onClick, data, badge }: AssetCardProps,
    ref: React.Ref<HTMLImageElement>
  ) {
    const theme = useTheme();
    const showOverlay = !!title || !!actionButtons;
    const activeSourceRef = useRef<string>(typeof source === 'string' ? source : source?.[0] || '');
    const sizeRef = useRef<{ width: number; height: number } | null>(null);
    const containerRef = useRef<HTMLImageElement | null>(null);

    const [isError, setIsError] = useState<boolean>(false);
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

    const onClickHandler = useCallback(
      (action?: AssetAction['action']) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        if (sizeRef.current === null) {
          if (containerRef.current === null) {
            return;
          }

          const boundingRect = containerRef.current.getBoundingClientRect();

          sizeRef.current = {
            width: boundingRect.width,
            height: boundingRect.height,
          };
        }

        onClick({
          action,
          source: activeSourceRef.current,
          details: { ...(data ?? {}), size: sizeRef.current },
        });
      },
      [onClick, data]
    );
    const onLoadHandler = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement, Event>, source: string) => {
        containerRef.current = event.currentTarget;
        setIsImageLoaded(true);
        activeSourceRef.current = source;
      },
      []
    );
    const onErrorHandler = useCallback(() => {
      setIsImageLoaded(true);

      if (
        data &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        'type' in data &&
        data?.type === 'Video'
      )
        return;

      setIsError(true);
    }, [setIsError, data]);

    const draggableHandlers = useDraggable({
      id: activeSourceRef.current,
      data: {
        type: ['canvas'],
        source: activeSourceRef.current,
        title,
        details: data,
      },
    });

    const draggableData = useMemo(() => {
      return draggable
        ? {
            ref: draggableHandlers.setNodeRef,
            ...draggableHandlers.listeners,
            ...draggableHandlers.attributes,
          }
        : {};
    }, [draggable, draggableHandlers]);

    if (isError) return null;

    return (
      <StyledWrapper {...draggableData} onClick={onClickHandler()} isImageLoaded={isImageLoaded}>
        <StyledImage>
          <Image
            source={source}
            sourceFallback={sourceFallback}
            rounded={true}
            onLoad={onLoadHandler}
            onError={onErrorHandler}
            ref={ref}
          />
        </StyledImage>

        {showOverlay && (
          <StyledOverlayWrapper>
            <StyleContentWrapper>
              {actionButtons && (
                <AssetActionButtons
                  color={theme.Color.NeutralsFixed.Paper}
                  type={'image'}
                  buttons={actionButtons}
                  onClick={onClickHandler}
                />
              )}
              {title && (
                <Text
                  size="Body"
                  color={theme.Color.NeutralsFixed.Paper}
                  truncate={true}
                  showTooltip={true}
                  maxWidth={theme.Card.Size.Inline.width - theme.Spacing.Size.XSmall * 2}
                >
                  {title}
                </Text>
              )}
              {badge && (
                <StyledBadge direction="bottom-left">
                  <Text size="Small" color={theme.Color.NeutralsFixed.Paper}>
                    {badge}
                  </Text>
                </StyledBadge>
              )}
            </StyleContentWrapper>
          </StyledOverlayWrapper>
        )}
      </StyledWrapper>
    );
  })
);
export interface TextCardProps {
  source: AssetCardProps['source'];
  title?: string;
  icon?: string;
  variant?: string;
  data: AssetAction['details'];
  actionButtons?: ActionButtons[];
  draggable: AssetCardProps['draggable'];
  onClick: AssetCardProps['onClick'];
  badge?: string;
}

export const TextCard = memo(
  forwardRef(function TextCard(
    { source, draggable, title, icon, variant, actionButtons, onClick, data, badge }: TextCardProps,
    ref: React.Ref<HTMLSpanElement>
  ) {
    const theme = useTheme();
    const textSource = (Array.isArray(source) ? source[0] : source) || '';
    const onClickHandler = useCallback(
      (action?: AssetAction['action']) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        onClick({
          action,
          source: textSource,
          details: data,
        });
      },
      [data, onClick, textSource]
    );
    const draggableHandlers = useDraggable({
      id: textSource,
      data: {
        type: ['canvas'],
        source,
        title: source,
        details: data,
      },
    });

    const draggableData = useMemo(() => {
      return draggable
        ? {
            ref: draggableHandlers.setNodeRef,
            ...draggableHandlers.listeners,
            ...draggableHandlers.attributes,
          }
        : {};
    }, [draggable, draggableHandlers]);

    return (
      <StyledWrapper {...draggableData} onClick={onClickHandler()} type="text">
        <StyleContentWrapper type="text" variant={variant}>
          <StyledTextWrapper>
            {icon && <Icon name={icon} color={theme.Color.Neutrals.Text} />}
            <Text ref={ref} size="Body" truncate={5} showTooltip={true}>
              {textSource.replace(/\u00A0/g, ' ')}
            </Text>
          </StyledTextWrapper>
        </StyleContentWrapper>

        {(title || actionButtons) && (
          <StyledTextActionButtons>
            {title && (
              <StyledTextLabelWrapper>
                <Text size="SmallHeavy" color={theme.Color.Neutrals.Text} truncate={true}>
                  {title}
                </Text>
              </StyledTextLabelWrapper>
            )}

            {actionButtons && (
              <AssetActionButtons
                color={theme.Color.Neutrals.Dark}
                type="text"
                buttons={actionButtons}
                onClick={onClickHandler}
              />
            )}
            {badge && (
              <StyledBadge direction="bottom-right">
                <Text size="Small" color={theme.Color.NeutralsFixed.Paper}>
                  {badge}
                </Text>
              </StyledBadge>
            )}
          </StyledTextActionButtons>
        )}
      </StyledWrapper>
    );
  })
);
